export const gettingStartedArticles = [
    {
        id: 'getting-started.welcome',
        title: 'Welcome to Costed.app',
        category: 'Getting Started',
        order: 1,
        summary: 'Introduction to Costed.app and its key features',
        content: `
Costed.app is a comprehensive Bill of Quantities management system designed for construction and project management professionals. It helps you manage job estimates, track orders, handle supplier relationships, and maintain financial control across your projects.

This cloud-based solution provides real-time collaboration, secure multi-tenant data isolation, and powerful reporting capabilities.

**Key capabilities include:**

- **Job Management** - Create and manage construction jobs with detailed estimates
- **Bill of Quantities** - Build comprehensive BOQs with catalogue items and adhoc entries
- **Order Management** - Generate purchase orders and track deliveries
- **Supplier Management** - Maintain supplier details, pricing, and compliance
- **Financial Tracking** - Invoice matching, payment processing, and progress claims
- **Document Management** - Store and organize project documentation
    `,
        expandableSections: [
            {
                title: 'System Requirements',
                content: `
**Browser Support:**
- Google Chrome (recommended)
- Microsoft Edge
- Mozilla Firefox
- Safari

**Screen Resolution:**
- Minimum: 1280 x 720
- Recommended: 1920 x 1080 or higher

**Internet Connection:**
- Broadband connection required
- Works offline with limited functionality
        `,
            },
            {
                title: 'Getting Help',
                content: `
**Within the Application:**
- Press **F1** or **Shift+?** to open this help panel
- Hover over buttons with ? icons for contextual help
- Use the search bar in the help panel to find specific topics

**Support:**
- Contact your system administrator for account issues
- Check the keyboard shortcuts panel (press ?) for quick actions
        `,
            },
        ],
        keywords: ['welcome', 'introduction', 'overview', 'getting started', 'help'],
        relatedArticles: ['getting-started.navigation', 'getting-started.first-job', 'features.keyboard-shortcuts'],
    },
    {
        id: 'getting-started.navigation',
        title: 'Navigating the Application',
        category: 'Getting Started',
        order: 2,
        summary: 'Learn how to navigate around Costed.app',
        content: `
Costed.app uses a sidebar navigation system to help you quickly access different areas of the application.

**Main Navigation:**

The sidebar on the left contains links to all major sections:

- **Dashboard** - Overview and quick actions
- **Jobs** - Job/project management
- **BOQ** - Bill of Quantities editor
- **Catalogue** - Master price catalogue
- **Orders** - Purchase order management
- **Suppliers** - Supplier database
- **Contacts** - Contact management
- **Accounting** - Financial transactions
- **Documents** - File management
- **Transmittals** - Document transmissions
- **Reports** - System reports
- **Recycle Bin** - Deleted items
- **Settings** - System configuration
    
**Tip — movable dialogs:** on desktop you can drag any dialog by its title bar to see the screen behind it. It snaps back to centre the next time it opens.
`,
        expandableSections: [
            {
                title: 'Sidebar Controls',
                content: `
**Collapse/Expand:**
Click the chevron icon at the top of the sidebar to collapse it to icons only. This gives you more screen space for content.

**Active Page:**
The currently active page is highlighted with a blue left border and background.
        `,
            },
            {
                title: 'Header Actions',
                content: `
The header bar provides:

- **Page Title** - Shows the current page name
- **Undo/Redo** - Undo or redo recent changes (where supported)
- **Save Indicator** - Shows when changes are being saved
- **Theme Toggle** - Switch between light, dark, and system theme
- **Notifications** - View system notifications
- **User Menu** - Profile and logout options
        `,
            },
        ],
        keywords: ['navigation', 'sidebar', 'menu', 'header', 'layout'],
        relatedArticles: ['getting-started.welcome', 'features.keyboard-shortcuts'],
    },
    {
        id: 'getting-started.first-job',
        title: 'Your First Job',
        category: 'Getting Started',
        order: 3,
        summary: 'Quick guide to creating and setting up your first job',
        content: `
This guide walks you through creating your first job in Costed.app.

**Step 1: Create the Job**

1. Navigate to **Jobs** from the sidebar
2. Click the **Add Job** button
3. Enter the job details:
   - Job Number (unique identifier)
   - Job Name
   - Client name
   - Address and contact details
4. Click **Save**

**Step 2: Add BOQ Items**

1. Navigate to **BOQ** from the sidebar
2. Select your new job from the dropdown
3. Use the **Catalogue** button to search and add items
4. Or use **Adhoc** to create custom line items
    `,
        expandableSections: [
            {
                title: 'Setting Up Cost Centres',
                content: `
Cost centres help organize your BOQ into logical sections (e.g., Preliminaries, Substructure, Superstructure).

1. In the BOQ page, use the Cost Centre panel on the left
2. Click **Add** to create a new cost centre
3. Drag items to reorder the hierarchy
4. Items automatically inherit the selected cost centre
        `,
            },
            {
                title: 'Creating Orders',
                content: `
Once you have BOQ items with suppliers assigned:

1. Navigate to **Orders**
2. Click **Create Order**
3. Select the job and supplier
4. Choose items to include
5. Review and send the order
        `,
            },
        ],
        keywords: ['first job', 'create job', 'new job', 'tutorial', 'quick start'],
        relatedArticles: ['jobs.create', 'boq.overview', 'orders.create'],
    },
];
